import axios from "axios";

// Confirmed by the user (2026-10-27): Courses come from the school's Moodle
// LMS via this custom PHP API — a separate external service, not this app's
// own Laravel backend — so it doesn't go through studentApi.js. Auth is a
// single shared token passed as a query param (`token`), not a Bearer header.
// In local dev, go through the CRA dev-server proxy (src/setupProxy.js) so
// the browser's request is same-origin and never hits the Moodle server's
// broken (duplicated) Access-Control-Allow-Origin header. Production has no
// such proxy layer, so it still hits the real URL directly and is still
// blocked until the Moodle team fixes that header.
const COURSE_API_URL =
  window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
    ? "/moodle-api"
    : "https://ecourse.asu.apeejay.edu/moodle-custom-api";
const COURSE_TOKEN = "11eba10d3544ac6d881143c0ecb59852";

// A dedicated instance instead of the shared `axios` default export — src/
// config.js sets organization-id/session-id/Content-Type on
// axios.defaults.headers.common globally for the staff app, and
// axios.create() still inherits whatever is on that object at creation time
// (this doesn't touch the global defaults — axios deep-clones headers into a
// new object on create()). This external Moodle API gets its own baseURL and
// a blank header slate instead: a non-simple Content-Type such as
// application/json forces the browser to send a CORS preflight (OPTIONS)
// first, and this API 405s on OPTIONS (confirmed 2026-11-04) — no preflight
// is handled, so any header that isn't one of the fetch spec's "simple"
// values must not leak in here.
const courseApi = axios.create({ baseURL: COURSE_API_URL });
courseApi.defaults.headers.common = {};

// This API sends real JSON but mislabels it as `Content-Type: text/html`
// (confirmed 2026-10-27), so axios's browser (XHR) adapter can hand back
// response.data as a raw string instead of auto-parsing it — unlike Node's
// http adapter, which parses it regardless of the declared content-type.
// Parse it ourselves whenever that happens.
courseApi.interceptors.response.use((response) => {
  if (typeof response.data === "string") {
    try {
      response.data = JSON.parse(response.data);
    } catch {
      // Not actually JSON — leave as-is and let the caller's own
      // defensive extraction handle an unexpected shape.
    }
  }
  return response;
});

export const getEnrollCourses = (idNumber) => {
  // The Authorization header was tried here on 2026-10-29 but reverted: it
  // turns this into a preflighted request, and the backend's duplicate
  // Access-Control-Allow-Origin header (reported to the team, still pending
  // as of 2026-10-29) blocks preflights the same way it blocks the plain
  // request — so it only adds a second failure mode with no benefit until
  // that's fixed. Re-add `headers: { Authorization: \`Bearer ${studentToken}\` } }`
  // once the backend confirms it's needed and the CORS fix has shipped.
  return courseApi.get(`/get_user_enroll_courses.php`, {
    params: {
      endpoint: "getEnrollCourses",
      idnumber: idNumber,
      token: COURSE_TOKEN,
    },
  });
};

export const getDetailedCourse = (courseId) => {
  return courseApi.get(`/get_course_content_details.php`, {
    params: {
      endpoint: "getCourseContent",
      courseid: courseId,
      token: COURSE_TOKEN,
    },
  });
};
